import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSound } from '../hooks/useSound';

const OrderSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId') || 'FS' + Date.now().toString().slice(-6);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const { play: playBoomSound } = useSound('/sounds/boom.mp3', { volume: 0.9 });

  useEffect(() => {
    // Sequence of animations
    const timer1 = setTimeout(() => setShowConfetti(true), 300);
    const timer2 = setTimeout(() => setShowFireworks(true), 800);
    const timer3 = setTimeout(() => setShowContent(true), 1500);

    // Play success sound if available
    playBoomSound();

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-accent-50 via-white to-yellow-50 relative overflow-hidden">
      {/* Animated Background Patterns */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -left-4 w-24 h-24 bg-yellow-200 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-20 right-10 w-16 h-16 bg-red-200 rounded-full opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-10 w-20 h-20 bg-blue-200 rounded-full opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute top-1/2 right-1/4 w-12 h-12 bg-green-200 rounded-full opacity-20 animate-float" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 animate-sparkle-burst"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#6c5ce7', '#a55eea'][Math.floor(Math.random() * 6)],
                animationDelay: `${Math.random() * 2}s`,
                borderRadius: Math.random() > 0.5 ? '50%' : '0%'
              }}
            />
          ))}
        </div>
      )}

      {/* Fireworks Animation */}
      {showFireworks && (
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${20 + i * 15}%`,
                top: `${20 + Math.random() * 30}%`,
              }}
            >
              {[...Array(12)].map((_, j) => (
                <div
                  key={j}
                  className="absolute w-1 h-8 bg-gradient-to-t from-yellow-400 via-red-500 to-transparent animate-firework-burst rounded-full"
                  style={{
                    transform: `rotate(${j * 30}deg)`,
                    transformOrigin: 'bottom center',
                    animationDelay: `${i * 0.5}s`
                  }}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12 relative z-10">
        <div className={`max-w-2xl mx-auto text-center transform transition-all duration-1000 ${
          showContent ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
        }`}>
          
          {/* Success Icon with Animation */}
          <div className="relative mb-8">
            <div className="w-32 h-32 mx-auto bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center shadow-2xl transform animate-elastic">
              <svg className="w-16 h-16 text-white animate-bounce-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            {/* Pulse Rings */}
            <div className="absolute inset-0 w-32 h-32 mx-auto rounded-full border-4 border-green-400 animate-ping opacity-20"></div>
            <div className="absolute inset-0 w-40 h-40 mx-auto rounded-full border-2 border-green-300 animate-ping opacity-10" style={{ animationDelay: '0.5s' }}></div>
          </div>

          {/* Success Message */}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4 animate-fade-in-up">
              🎉 Enquiry Placed Successfully!
            </h1>
            <p className="text-xl text-neutral-600 mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Thank you for your enquiry! Your fireworks request is confirmed and being processed.
            </p>
          </div>

          {/* Order Details Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-neutral-100 animate-slide-up" style={{ animationDelay: '0.4s' }}>
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-neutral-900">Enquiry #{orderId}</h2>
                <p className="text-neutral-500">Placed on {new Date().toLocaleDateString('en-IN', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</p>
              </div>
            </div>

            {/* Status Steps */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-2 animate-scale-in">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <p className="text-sm font-semibold text-green-600">Enquiry Placed</p>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mb-2 animate-pulse">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <p className="text-sm font-semibold text-yellow-600">Payment Verification</p>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-neutral-300 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-neutral-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 00-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-neutral-500">Processing</p>
              </div>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 bg-neutral-300 rounded-full flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-neutral-500" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium text-neutral-500">Shipped</p>
              </div>
            </div>

            {/* Important Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Payment Verification in Progress</p>
                  <p>We are verifying your payment screenshot. Your order will be processed once verification is complete. You'll receive updates via email and SMS.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <Link
              to="/enquiries"
              className="bg-accent-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-accent-700 hover:shadow-xl hover:shadow-accent-500/25 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              View My Enquiries
            </Link>
            
            <Link
              to="/"
              className="bg-white text-neutral-700 border-2 border-neutral-200 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-neutral-50 hover:border-neutral-300 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Continue Shopping
            </Link>
          </div>

          {/* Celebratory Message */}
          <div className="mt-8 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
            <p className="text-lg text-neutral-600 mb-2">🎆 Get ready for an amazing celebration! 🎆</p>
            <p className="text-sm text-neutral-500">Your fireworks will bring joy and excitement to your special occasions.</p>
          </div>
        </div>
      </div>

      {/* Floating Firework Emojis */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {['🎆', '🧨', '✨', '🎇', '💥'].map((emoji, i) => (
          <div
            key={i}
            className="absolute text-4xl animate-float opacity-60"
            style={{
              left: `${10 + i * 20}%`,
              top: `${20 + (i % 3) * 20}%`,
              animationDelay: `${i * 0.5}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          >
            {emoji}
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderSuccess;